/**
 * useOsmdPlayback
 *
 * Audio-Player-Teil der interaktiven Vorschau (osmd-extended `PlaybackManager`): Binden des Players
 * an eine OSMD-Instanz, Initialisierung pro geladenem Sheet, Transport (Play/Pause/Stop), Tempo,
 * Metronom und der Instrument-Mixer. Sub-Composable von {@link @/composables/useOsmdInteractiveScore}
 * – dort wird es mit der Instanz verdrahtet und in den ScoreEditContext eingespeist.
 *
 * Bausteine (siehe die jeweiligen Kopfkommentare):
 * - {@link getSharedAudioPlayer}   – app-weit EIN Audio-Player (AudioContext + SoundFont-Cache).
 * - {@link PerformanceTimingSource} – Zeitquelle ohne eigenen AudioContext.
 * - `stores/playbackStore.ts`      – SoundFont-Bereitschaft (reaktiv) + `IPlaybackListener`.
 *
 * Kritische Punkte (nicht rückbauen):
 * - `DoPreCount = false` direkt nach der Konstruktion: der PlaybackManager zählt sonst standardmäßig
 *   einen vollen Takt vor – mit Metronom-Lautstärke 0, also als reine Stille (≈1,4 s bei 170 BPM
 *   4/4). Jedes `reset()`/`setPlaybackStart()` armt ihn erneut, deshalb muss das Flag VOR dem
 *   ersten `reset()` stehen.
 * - `play()` wartet auf die SoundFonts: `initialize()` lädt sie un-awaited, `playSound()` wirft bis
 *   dahin still → ohne Warten fallen die ersten Noten weg.
 * - `bindSheet()` initialisiert genau einmal pro geladenem `MusicSheet` (Identitätsvergleich);
 *   Re-Renders derselben Partitur (Resize/Zoom) laufen nur durch `afterRender()`.
 */
import {computed, ref} from 'vue';
import {
    type GraphicalNote,
    type Instrument,
    type MusicSheet,
    OpenSheetMusicDisplay,
    PlaybackManager,
} from 'opensheetmusicdisplay';
import {usePlaybackStore} from '@/stores/playbackStore.ts';
import {CORE_SOUNDFONTS, getSharedAudioPlayer, requiredSoundFontsFor} from '@/composables/osmd/playbackEngine';
import {BASS_PREVIEW_PART_NAME} from '@/composables/musicXmlDom';
import {PerformanceTimingSource} from '@/composables/osmd/performanceTimingSource';

/** A single mixer channel as consumed by the playback UI (decoupled from OSMD's Instrument). */
/**
 * Vorgabe für das Bass-Vorschau-System, solange der Nutzer nichts eingestellt hat.
 *
 * Der Wechselbass ist Begleitung – auf voller Lautstärke deckt er die Melodie zu. Ersetzt die
 * frühere **notierte** `dynamics.Dynamic('mp')` auf dem Part: die stand im Notenbild, war nur
 * durch Neuerzeugen änderbar, und OSMD schreibt bei jeder Dynamik mitten im Abspielen die
 * Kanal-Lautstärke neu (`audioPlayer.setVolume`) – sie arbeitete also gegen jeden Regler.
 */
export const BASS_PREVIEW_DEFAULT_VOLUME = 0.7;

export interface MixerTrack {
    id: number;
    name: string;
    volume: number;
    muted: boolean;
    solo: boolean;
}

export function useOsmdPlayback(
    getOsmd: () => OpenSheetMusicDisplay | null,
    getSelectedGraphicalNote?: () => GraphicalNote | null,
) {
    let playback: PlaybackManager | null = null;
    let timingSource: PerformanceTimingSource | null = null;
    /** Das Sheet, für das `initialize()` zuletzt lief – macht `bindSheet()` idempotent pro Sheet. */
    let boundSheet: MusicSheet | null = null;
    let dummySoundPlayed = false;
    /** Laufende Nummer des letzten Transport-Befehls; ein `play()` bricht ab, wenn sie sich während
     *  des Wartens auf die SoundFonts geändert hat (Stop/Pause/Dispose dazwischen). */
    let transportToken = 0;

    const isPlayerReady = ref(false);
    const isPlaying = ref(false);
    const currentBpm = ref(120);
    const metronomeEnabled = ref(false);
    const tracks = ref<MixerTrack[]>([]);
    const playbackStore = usePlaybackStore();
    const isSoundReady = computed(() => playbackStore.isSoundReady);
    const isLoadingSounds = computed(() => playbackStore.isLoadingSounds);
    const soundLoadLabel = computed(() => playbackStore.soundLoadLabel);

    function requiredSoundFonts(): readonly number[] {
        const sheet = getOsmd()?.Sheet;
        return sheet ? requiredSoundFontsFor(sheet) : CORE_SOUNDFONTS;
    }

    /**
     * Wire up the audio player (osmd-extended) for a freshly created OSMD instance. The
     * metronome- and message-viewer arguments have no public concrete implementation; the
     * official demos pass `undefined` there (cast precisely to the constructor's parameter
     * types, no `any`).
     */
    function createPlayback(osmd: OpenSheetMusicDisplay): void {
        timingSource = new PerformanceTimingSource();
        playback = new PlaybackManager(
            timingSource,
            undefined as unknown as ConstructorParameters<typeof PlaybackManager>[1],
            getSharedAudioPlayer(),
            undefined as unknown as ConstructorParameters<typeof PlaybackManager>[3],
        );
        playback.DoPreCount = false;
        playback.DoPlayback = true;
        osmd.PlaybackManager = playback;
        // SoundFonts schon jetzt laden (erste Vorschau = Nutzer hat eine Datei), damit Play sofort geht.
        void playbackStore.ensureSoundsLoaded(CORE_SOUNDFONTS);
    }

    /** Initialize the player for the currently loaded sheet – exactly once per `MusicSheet`. */
    function bindSheet(): void {
        const osmd = getOsmd();
        if (!osmd || !osmd.Sheet || !playback) return;
        if (boundSheet === osmd.Sheet) return;
        try {
            void playback.pause();
            transportToken++;
            // OSMDs render() legt die Cursor selbst an und registriert sie als Listener; nur falls
            // (noch) keiner existiert, nachholen.
            if (!osmd.cursor) osmd.enableOrDisableCursors(true);
            playback.initialize(osmd.Sheet.MusicPartManager);
            // TimingSource mit den (bereits vom MusicSheet erzeugten) PlaybackSettings verbinden,
            // sonst ist timingSource.Settings undefined → Crash in PlaybackManager.loop (getDurationInMs).
            if (timingSource) {
                timingSource.Settings = osmd.Sheet.SheetPlaybackSetting;
            }
            const bpm = Math.round(playback.OriginalBpm) || osmd.Sheet.SheetPlaybackSetting?.BeatsPerMinute || 120;
            playback.bpmChanged(bpm, false);
            currentBpm.value = bpm;
            playback.reset();
            playback.Metronome.Audible = metronomeEnabled.value;
            syncTracks();

            playback.addListener(playbackStore.getPlaybackListener());
            void playbackStore.ensureSoundsLoaded(requiredSoundFontsFor(osmd.Sheet));
            boundSheet = osmd.Sheet;
            isPlaying.value = false;
            isPlayerReady.value = true;
        } catch (e) {
            console.error('[OSMD Player] Failed to initialize playback', e);
            boundSheet = null;
            isPlayerReady.value = false;
        }
    }

    /** Nach jedem Render (auch Resize/Zoom): kein `initialize()`, nur den Cursor bei laufender
     *  Wiedergabe wieder einblenden (OSMD legt ihn beim Render neu an). */
    function afterRender(): void {
        if (isPlaying.value) getOsmd()?.cursor?.show();
    }

    // ============ Transport ============

    /**
     * Setzt den Playback-Startpunkt (Iterator + Cursor + Sheet.SelectionStart) auf die zuletzt
     * ausgewählte Note (`getSelectedGraphicalNote`). Ohne Auswahl → `reset()` auf den bisherigen
     * SelectionStart (i. d. R. Stückanfang). Der Timestamp ist auch für Pausen gültig. Guard +
     * try/catch, da der Aufruf (via Auswahl-Watcher) theoretisch vor `initialize()` feuern kann.
     */
    function setPlaybackStartToSelection(): void {
        if (!playback) return;
        try {
            const ts = getSelectedGraphicalNote?.()?.sourceNote?.getAbsoluteTimestamp?.();
            if (ts) playback.setPlaybackStart(ts);
            else playback.reset();
        } catch (e) {
            console.warn('[OSMD Player] setPlaybackStartToSelection failed', e);
        }
    }

    async function play(): Promise<void> {
        if (!playback || !isPlayerReady.value) return;
        const token = ++transportToken;
        // Innerhalb der User-Geste: AudioContext fortsetzen (Autoplay-Policy) – das Original setzt
        // nur die Timing-Uhr fort, nie den Kontext des Audio-Players.
        await playbackStore.resumeAudio();
        // First gesture must unlock the audio context (iOS); harmless elsewhere.
        if (!dummySoundPlayed) {
            playback.playDummySound();
            dummySoundPlayed = true;
        }
        // Erst spielen, wenn die SoundFonts da sind – sonst verschluckt playSound() die ersten Noten.
        await playbackStore.ensureSoundsLoaded(requiredSoundFonts());
        if (token !== transportToken || !playback) return;
        // Playback-Cursor für die Wiedergabe einblenden (im Leerlauf versteckt).
        getOsmd()?.cursor?.show();
        await playback.play();
        isPlaying.value = true;
    }

    async function pause(): Promise<void> {
        if (!playback) return;
        transportToken++;
        await playback.pause();
        isPlaying.value = false;
    }

    async function stop(): Promise<void> {
        if (!playback) return;
        transportToken++;
        await playback.pause();
        // Stop springt zur zuletzt ausgewählten Note zurück (bzw. zum Anfang, wenn keine Auswahl).
        setPlaybackStartToSelection();
        isPlaying.value = false;
    }

    function setBpm(bpm: number): void {
        if (!playback) return;
        currentBpm.value = bpm;
        playback.bpmChanged(bpm, true);
    }

    function setMetronome(enabled: boolean): void {
        if (!playback) return;
        metronomeEnabled.value = enabled;
        playback.Metronome.Audible = enabled;
    }

    // ============ Mixer ============

    function syncTracks(): void {
        const osmd = getOsmd();
        if (!osmd?.Sheet) {
            tracks.value = [];
            return;
        }
        tracks.value = osmd.Sheet.Instruments.map((inst: Instrument, idx: number) => {
            const name = inst.Name || `Instrument ${idx + 1}`;
            return {
                id: idx,
                name,
                volume: playbackStore.rememberedTrackVolume(name) ?? defaultVolumeFor(name, inst),
                muted: !inst.Audible,
                solo: inst.Solo,
            };
        });
        // Die Spurliste entsteht bei JEDEM neu geladenen Sheet neu – also bei jedem Bass-Erzeugen,
        // Bearbeiten und Transponieren. Ohne dieses erneute Anwenden stünde der Player danach
        // wieder auf Anschlag, obwohl der Regler etwas anderes anzeigt.
        for (const track of tracks.value) applyTrackVolume(track.id, track.volume);
    }

    /** OSMDs `Instrument.Volume`-Getter greift blind auf `subInstruments[0]` zu und wirft sonst. */
    function readVolume(inst: Instrument): number {
        try {
            const value = inst.Volume;
            return typeof value === 'number' && Number.isFinite(value) ? value : 1;
        } catch {
            return 1;
        }
    }

    function defaultVolumeFor(name: string, inst: Instrument): number {
        return name === BASS_PREVIEW_PART_NAME ? BASS_PREVIEW_DEFAULT_VOLUME : readVolume(inst);
    }

    /**
     * Lautstärke auf den Player anwenden – **ohne** sie als Nutzerwunsch zu merken.
     *
     * `volumeChanged` erwartet OSMDs Skala **0–100** (`Instrument.Volume = v/100`), unsere UI führt
     * 0–1. Ohne die Umrechnung landet jede Spur bei höchstens 0.01 und ist damit praktisch stumm –
     * umgerechnet wird ausschließlich hier, an der Bibliotheksgrenze.
     */
    function applyTrackVolume(id: number, volume: number): void {
        const inst = getOsmd()?.Sheet?.Instruments[id];
        if (!inst || !playback) return;
        inst.Volume = volume;
        playback.volumeChanged(id, volume * 100);
    }

    function setTrackVolume(id: number, volume: number): void {
        const track = tracks.value[id];
        applyTrackVolume(id, volume);
        if (track) {
            track.volume = volume;
            playbackStore.rememberTrackVolume(track.name, volume);
        }
    }

    function setTrackMuted(id: number, muted: boolean): void {
        const inst = getOsmd()?.Sheet?.Instruments[id];
        if (!inst || !playback) return;
        inst.Audible = !muted;
        if (muted) playback.volumeMute(id);
        else playback.volumeUnmute(id);
        const track = tracks.value[id];
        if (track) track.muted = muted;
    }

    function setTrackSolo(id: number, solo: boolean): void {
        const inst = getOsmd()?.Sheet?.Instruments[id];
        if (!inst) return;
        inst.Solo = solo;
        playback?.checkForSoloDeactivated();
        const track = tracks.value[id];
        if (track) track.solo = solo;
    }

    /** Nur den Manager dieser Instanz entsorgen – der Audio-Player ist geteilt und lebt weiter. */
    function disposePlayer(): void {
        transportToken++;
        try {
            void playback?.pause();
            playback?.Dispose();
        } catch {
            /* ignore */
        }
        playback = null;
        timingSource = null;
        boundSheet = null;
        dummySoundPlayed = false;
        isPlaying.value = false;
        isPlayerReady.value = false;
        tracks.value = [];
    }

    return {
        // State
        isPlayerReady,
        isPlaying,
        isSoundReady,
        isLoadingSounds,
        soundLoadLabel,
        currentBpm,
        metronomeEnabled,
        tracks,
        // Lifecycle
        createPlayback,
        bindSheet,
        afterRender,
        disposePlayer,
        // Transport + Mixer
        play,
        pause,
        stop,
        setPlaybackStartToSelection,
        setBpm,
        setMetronome,
        setTrackVolume,
        setTrackMuted,
        setTrackSolo,
    } as const;
}
